import React from "react";
import {
  currencyRateConversion,
  formatAmount,
} from "../../utils/hooks/dataHandler";
import {
  autoTextColor,
  beautyAutoLine,
  boldAutoColumn,
  buildCalculatorNotes,
} from "../../utils/pdf/page";
import PdfBase from "../Schedules/Others/Pdf/PdfBase";
import {
  buildAutoNote,
  buildAutoTable,
  enhanceReferenceColumn,
  formatReferenceDetails,
} from "../../utils/pdf/buildAutoTable";
import { BasePdfGenerator } from "../../utils/pdf/BasePdfGenerator";
import ExcelBase from "../Schedules/Others/Excel/ExcelBase";
import {
  buildIrasSectionSpacer,
  getIrasNoteTableOptions,
  getIrasTableStyles,
  IRAS_TABLE_SPACING_DISPLAY_PRESETS,
  normaliseIrasAutoTextColumnSpacing,
} from "../MainTaxComputation/Pdf/iras/tableSpacing";

export const generateDetailedPlPdf = (
  forView,
  company,
  revenue,
  otherIncome,
  costOfSales,
  expenses,
  sectionTotals,
  currencyView,
  notes,
  reviewNotes,
  flipSignMode,
  isExcel = false,
  options = {}
) => {
  const pdf = new BasePdfGenerator(false, isExcel);
  const tableSpacingOptions = {
    density: options?.pdfDensity,
    overrides: options?.pdfSpacingOptions,
  };

  let contentColumns = ["", currencyView, currencyView, "Sch"];
  const isNonSgdCurrency = currencyView !== "SGD";
  const exchangeRate = company?.currencyRate ?? 1;
  const getExchangeRate = (rate) => {
    const parsedRate = parseFloat(rate);
    return parsedRate > 0 ? parsedRate : parseFloat(exchangeRate) || 1;
  };
  const toSgdAmount = (amount, rate) =>
    formatAmount(
      Math.round(currencyRateConversion(amount, getExchangeRate(rate), "SGD"))
    );

  if (isNonSgdCurrency) contentColumns.push("SGD");

  if (forView) contentColumns.push(autoTextColor("Reference"));

  const columnCount = contentColumns.length;
  const pushSectionSpacer = () => {
    if (!isExcel) {
      contentData.push(
        buildIrasSectionSpacer(columnCount, tableSpacingOptions)
      );
    }
  };

  const getSignedAmount = (row, sectionName) =>
    flipSignMode[sectionName] === "negative" ? -row.amount : row.amount;

  const getSgdSectionTotal = (data, sectionName) =>
    data.reduce((sum, row) => {
      const amount = parseFloat(getSignedAmount(row, sectionName));
      if (Number.isNaN(amount)) return sum;

      return (
        sum +
        currencyRateConversion(amount, getExchangeRate(row.exchangeRate), "SGD")
      );
    }, 0);

  let totalLines = [];
  let totalSubLines = [];
  let totalDoubleLines = [];

  let contentData = [];

  const processRow = (data, sectionName) => {
    contentData.push([boldAutoColumn(sectionName)]);

    data.forEach((row) => {
      let content = [row.name || ""];

      const signedAmount = getSignedAmount(row, sectionName);
      const amount = formatAmount(signedAmount);

      content.push(amount);

      content.push("");
      if (
        row.taxAdjustment &&
        !["Added Back", "Deduct", "None"].includes(row.taxAdjustment)
      ) {
        // Case: Tax Adjustment is not "Added Back", "Deduct", or "None"
        if (row.schedule) {
          if (
            row.schedule.adjustment &&
            ["Added Back", "Deduct"].includes(row.schedule.adjustment)
          ) {
            // If row.schedule.adjustment is "Added Back" or "Deduct"
            content.push(
              `${row.schedule.adjustment} / Sch ${row.schedule.scheduleNo}`
            );
          } else {
            // If `row.schedule` exists but no valid adjustment, display schedule number
            content.push(
              `Sch ${JSON.stringify(row.schedule.scheduleNo)
                .replace('"', "")
                .replace('"', "")}`
            );
          }
        } else {
          // If no schedule, display an empty string
          content.push("");
        }
      } else {
        content.push(row.taxAdjustment);
      }

      if (isNonSgdCurrency) {
        content.push(toSgdAmount(signedAmount, row.exchangeRate));
      }

      if (forView) {
        content.push(autoTextColor(row.reference || ""));
      }

      contentData.push(content);
    });
    totalLines.push(contentData.length - 1);

    const totalRow = [
      boldAutoColumn(`Total ${sectionName}`),
      "",
      formatAmount(sectionTotals[sectionName]),
    ];

    if (isNonSgdCurrency) {
      totalRow.push("", toSgdAmount(getSgdSectionTotal(data, sectionName), 1));
    }

    contentData.push(totalRow);
  };

  let checkIfHasGrossSection = false;

  if (revenue && revenue.length > 0) {
    processRow(revenue, "Revenue");
    pushSectionSpacer();
    checkIfHasGrossSection = true;
  }

  if (costOfSales && costOfSales.length > 0) {
    processRow(costOfSales, "Cost of Sales");
    pushSectionSpacer();
    checkIfHasGrossSection = true;
  }

  if (checkIfHasGrossSection) {
    const grossProfit =
      (sectionTotals["Revenue"] || 0) + (sectionTotals["Cost of Sales"] || 0);

    const grossProfitRow = [
      boldAutoColumn("Gross Profit"),
      "",
      formatAmount(grossProfit),
    ];

    if (isNonSgdCurrency) {
      const grossProfitSgd =
        getSgdSectionTotal(revenue || [], "Revenue") +
        getSgdSectionTotal(costOfSales || [], "Cost of Sales");

      grossProfitRow.push("", toSgdAmount(grossProfitSgd, 1));
    }

    contentData.push(grossProfitRow);

    totalSubLines.push(contentData.length - 2);

    pushSectionSpacer();
  }

  if (otherIncome && otherIncome.length > 0) {
    processRow(otherIncome, "Other Income");
    pushSectionSpacer();
  }

  if (expenses && expenses.length > 0) {
    processRow(expenses, "Expenses");
    pushSectionSpacer();
  }

  const netProfitBeforeTax =
    (sectionTotals["Revenue"] || 0) +
    (sectionTotals["Other Income"] || 0) +
    (sectionTotals["Cost of Sales"] || 0) +
    (sectionTotals["Expenses"] || 0);

  const netProfitBeforeTaxRow = [
    boldAutoColumn("Net Profit / (Loss) before Tax"),
    "",
    formatAmount(netProfitBeforeTax),
  ];

  if (isNonSgdCurrency) {
    const netProfitBeforeTaxSgd =
      getSgdSectionTotal(revenue || [], "Revenue") +
      getSgdSectionTotal(otherIncome || [], "Other Income") +
      getSgdSectionTotal(costOfSales || [], "Cost of Sales") +
      getSgdSectionTotal(expenses || [], "Expenses");

    netProfitBeforeTaxRow.push("", toSgdAmount(netProfitBeforeTaxSgd, 1));
  }

  const netProfitBeforeTaxRowIndex = contentData.length;
  contentData.push(netProfitBeforeTaxRow);

  if (isExcel) totalSubLines.push(contentData.length - 2);
  totalSubLines.push(contentData.length - 1);
  totalDoubleLines.push(contentData.length - 1);

  const refColumnIndex = isNonSgdCurrency ? 5 : 4;
  if (forView && contentData.length > 0) {
    const { body: enhancedBody, referenceDetails } = enhanceReferenceColumn(
      contentData,
      refColumnIndex,
      15
    );
    contentData = enhancedBody;
    normaliseIrasAutoTextColumnSpacing(
      contentData,
      refColumnIndex,
      tableSpacingOptions
    );
    const refDetailsText = formatReferenceDetails(referenceDetails);
    if (refDetailsText) {
      if (reviewNotes !== "") reviewNotes += "\n\n";
      reviewNotes += "[Reference Details]\n" + refDetailsText;
    }
  }

  if (contentData.length > 0)
    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      customStartY: 55,
      schedule: {
        name: "Detailed Income Statement",
      },
      company,
      styles: getIrasTableStyles(pdf, tableSpacingOptions),
      columnStyles: isNonSgdCurrency
        ? {
            0: { halign: "left", minCellWidth: 39 },
            1: { halign: "right", cellWidth: 25 },
            2: { halign: "right", cellWidth: 25 },
            3: { halign: "center", cellWidth: 24 },
            4: { halign: "right", cellWidth: 25 },
            5: { halign: "center" },
          }
        : {
            0: { halign: "left", minCellWidth: 60 },
            1: { halign: "right", cellWidth: 25 },
            2: { halign: "right", cellWidth: 25 },
            3: { halign: "center" },
            4: { halign: "center" },
          },
      didDrawCell: function (data) {
        if (
          data.row.section === "body" &&
          data.row.index === netProfitBeforeTaxRowIndex &&
          data.column.index === 2
        ) {
          const lineWidth = data.cell.width * 0.95;
          const cellCenterX = data.cell.x + data.cell.width / 2;
          pdf.doc.setDrawColor(0);
          pdf.doc.setLineWidth(0.1);
          pdf.doc.line(
            cellCenterX - lineWidth / 2,
            data.cell.y,
            cellCenterX + lineWidth / 2,
            data.cell.y
          );
        }

        beautyAutoLine(
          data,
          pdf.doc,
          (totalLines.includes(data.row.index) &&
            [1].includes(data.column.index)) ||
            (totalSubLines.includes(data.row.index) &&
              [2].includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            [2].includes(data.column.index)
        );
      },
      lineSettings: {
        totalLines: totalSubLines,
        subTotalLines: totalLines,
        totalDoubleLines,
        totalRows: [2],
        subTotalRows: [1],
      },
    });

  if (forView) {
    const mergedRows = revenue
      .concat(costOfSales, otherIncome, expenses)
      .filter((row) => row.history && row.history.length > 1);

    if (mergedRows.length > 0) {
      if (reviewNotes !== "") reviewNotes += "\n\n";
      reviewNotes += "[Account Merge Summary]";

      mergedRows.forEach((row) => {
        reviewNotes += `\n${row.name}`;

        // Show the history breakdown for each row
        const historyBreakdown = row.history
          .map((historyItem) => `${formatAmount(historyItem.amount)}`)
          .join(" + ");
        const historyItemBreakdown = row.history
          .map((historyItem) => `${historyItem.name}`)
          .join(" + ");
        const totalHistory = `${historyBreakdown}`;
        reviewNotes += `\n=${historyItemBreakdown}`;
        reviewNotes += `\n=${totalHistory}`;
        reviewNotes += `\n= ${formatAmount(row.amount)}`;
      });

      reviewNotes += "\n";
    }

    reviewNotes = buildCalculatorNotes(
      reviewNotes,
      revenue.concat(costOfSales, otherIncome, expenses)
    );
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    {
      name: "Detailed Income Statement",
    },
    notes,
    reviewNotes,
    [],
    false,
    "Notes/Comments",
    getIrasNoteTableOptions(pdf, tableSpacingOptions)
  );

  return pdf.getDoc(forView, company, { name: "Detailed Income Statement" });
};

const PdfGenerator = ({
  company,
  revenue,
  otherIncome,
  costOfSales,
  expenses,
  sectionTotals,
  currencyView,
  notes,
  reviewNotes,
  flipSignMode,
}) => {
  const sharedArgs = [
    company,
    revenue,
    otherIncome,
    costOfSales,
    expenses,
    sectionTotals,
    currencyView,
    notes,
    reviewNotes,
    flipSignMode,
  ];
  return (
    <>
      <PdfBase
        company={company}
        generatePdf={(forView, options) =>
          generateDetailedPlPdf(forView, ...sharedArgs, false, options)
        }
        downloadName='Detailed Income Statement'
        enableDraftView={true}
        pdfDensityOptions={{
          defaultValue: "default",
          options: [
            { value: "default", label: "Default" },
            { value: "compact", label: "Compact" },
          ],
        }}
        pdfSpacingPresets={IRAS_TABLE_SPACING_DISPLAY_PRESETS}
      />
      <ExcelBase
        generateExcel={(forView) =>
          generateDetailedPlPdf(forView, ...sharedArgs, true)
        }
      />
    </>
  );
};

export default PdfGenerator;
